import NiceModal from '@ebay/nice-modal-react';
import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  balloonActions,
  BalloonStatus,
  selectBalloonLoading,
  selectBalloonMultiplier,
  selectBalloonPauseRemainingMs,
  selectBalloonStatus,
} from '@/entities/balloon';
import { selectUserBalanceByCurrency } from '@/entities/user';
import { MODALS } from '@/shared/config/modals';
import type { CurrencyCode } from '@/shared/lib/currency';

import { BalloonTooltip } from './BalloonTooltip';

const PAUSE_TOTAL_MS = 5000;

interface BalloonHeatButtonProps {
  betAmount: number;
  currency: CurrencyCode;
}

export function BalloonHeatButton({ betAmount, currency }: BalloonHeatButtonProps) {
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectBalloonStatus);
  const multiplier = useAppSelector(selectBalloonMultiplier);
  const pauseRemainingMs = useAppSelector(selectBalloonPauseRemainingMs);
  const loading = useAppSelector(selectBalloonLoading);
  const balance = useAppSelector(selectUserBalanceByCurrency(currency));

  const isPressedRef = useRef(false);

  const isIdle = status === BalloonStatus.IDLE;
  const isPreheating = status === BalloonStatus.PREHEATING;
  const isReady = status === BalloonStatus.READY;
  const isHeating = status === BalloonStatus.HEATING;
  const isPaused = status === BalloonStatus.PAUSED;
  const isOver = status === BalloonStatus.WON || status === BalloonStatus.BUSTED;

  const heatActive = isPreheating || isHeating;
  const showTooltip = !isHeating && !isReady && !isOver;

  const pauseSeconds = pauseRemainingMs != null ? (pauseRemainingMs / 1000).toFixed(1) : null;
  const tooltipVariant = isPreheating ? 'preheating' : isPaused ? 'paused' : 'idle';
  const tooltipIcon = isPreheating ? '/assets/balloon/fire.png' : '/assets/balloon/finger.png';
  const tooltipHeadline = isPreheating
    ? 'Pre-heating balloon...'
    : isPaused
      ? 'Keep Holding!'
      : 'Press to bet!';
  const tooltipSub = isPaused && pauseSeconds ? `Auto cashout in ${pauseSeconds}s` : null;
  const tooltipProgressPct = isPreheating
    ? multiplier * 100
    : isPaused && pauseRemainingMs != null
      ? (pauseRemainingMs / PAUSE_TOTAL_MS) * 100
      : 0;

  // If server enters preheating/heating while button was already released, cancel/pause
  useEffect(() => {
    if ((isPreheating || isHeating) && !isPressedRef.current) {
      dispatch(balloonActions.heatStop());
    }
  }, [isPreheating, isHeating, dispatch]);

  const isGift = currency === 'GIFT';

  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (isOver || loading || (!isGift && betAmount > balance)) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    isPressedRef.current = true;
    if (isIdle) {
      if (isGift) {
        NiceModal.show(MODALS.GIFT_SELECT, {
          onSelect: (gift: { id: string }) =>
            dispatch(balloonActions.startGame({ betAmount: 0, currency, giftId: gift.id })),
        });
        return;
      }
      dispatch(balloonActions.startGame({ betAmount, currency }));
    } else if (isPaused) {
      dispatch(balloonActions.heatStart());
    }
  };

  const handlePointerUp = () => {
    isPressedRef.current = false;
    if (isPreheating || isHeating) dispatch(balloonActions.heatStop());
  };

  const handlePointerCancel = () => {
    isPressedRef.current = false;
    if (isPreheating || isHeating) dispatch(balloonActions.heatStop());
  };

  return (
    <div className="balloon-panel__heat-wrap">
      <button
        className={['balloon-panel__heat', heatActive && 'balloon-panel__heat--active']
          .filter(Boolean)
          .join(' ')}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        disabled={isOver || loading || betAmount > balance}>
        HEAT
      </button>

      {showTooltip && (
        <BalloonTooltip
          variant={tooltipVariant}
          icon={tooltipIcon}
          headline={tooltipHeadline}
          sub={tooltipSub}
          bounce={isIdle}
          progressPct={tooltipProgressPct}
          showProgress={isPreheating || isPaused}
        />
      )}
    </div>
  );
}
